import SEOHelmet from '@/components/SEOHelmet';
import { FC, useCallback } from 'react';
import styles from './orgs-management.module.scss';
import ChevronLeftIcon from '@/assets/icons/chevron-left.svg?react';
import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import { classnames } from '@/utils/functions';
import MsgParagraph from '@/components/MsgParagraph';
import GearIcon from '@/assets/icons/gear.svg?react';
import { AdminService } from '@/services/admin.service';
import Modal from '@/components/Modal/modal';
import Button from '@/components/Button';
import useSession from '@/hooks/useSession';
import { Organization } from '@/models/user';
import OrganizationForm from './components/OrganizationForm';
import SeatInfo from '@/components/SeatInfo';

const OrgsManagement = () => {
  const [query, setQuery] = useState<string>('');
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Organization | undefined>(
    undefined
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [sortDesc, setSortDesc] = useState<boolean>(true);
  useState<Organization | null>(null);
  const { refreshSession } = useSession();
  const [sortColumn, setSortColumn] =
    useState<keyof Organization>('created_at');
  const [openModal, setOpenModal] = useState<'new' | 'edit' | null>(null);
  // State setter passed to child component; value unused in parent
  const [, setIsSSOEnabled] = useState(false);
  const [isGuideVisible, setIsGuideVisible] = useState(false);
  const { orgIndex } = useSession();
  const columns: { label: string; value: keyof Organization }[] = [
    { label: 'Date', value: 'created_at' },
    { label: 'Name', value: 'name' },
    { label: 'Slug', value: 'slug' },
    { label: 'Suggestions', value: 'suggestions' },
  ];

  const getOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const fetchedOrgs = await AdminService.getOrganizations();
      setOrgs(fetchedOrgs);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const onSearch: (
    e: FormEvent<HTMLFormElement> | ChangeEvent<HTMLInputElement>
  ) => void = (e) => {
    let search = '';
    if (e.type === 'submit') {
      e.preventDefault();
      const formData = new FormData(
        (e as FormEvent<HTMLFormElement>).currentTarget
      );
      search = formData.get('search') as string;
    } else {
      search = (e as ChangeEvent<HTMLInputElement>).target.value;
    }

    setQuery(search);
  };

  const onSortingChange = (column: keyof Organization) => {
    setSortColumn(column);
    if (sortColumn === column) {
      setSortDesc((current) => !current);
    } else {
      setSortDesc(true);
    }
  };

  const filteredOrgs = useMemo(() => {
    return orgs.filter((org) =>
      org.name.toLowerCase().includes(query.toLowerCase())
    );
  }, [orgs, query]);

  const sortedOrgs = useMemo(() => {
    return filteredOrgs.sort((a, b) => {
      let result = 0;

      if (
        typeof a[sortColumn] === 'undefined' ||
        typeof b[sortColumn] === 'undefined'
      ) {
        return 0;
      }

      if (a[sortColumn] < b[sortColumn]) {
        result = -1;
      } else if (a[sortColumn] > b[sortColumn]) {
        result = 1;
      } else if (a.created_at && b.created_at) {
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      }

      return (sortColumn === 'created_at' ? !sortDesc : sortDesc)
        ? result
        : -result;
    });
  }, [filteredOrgs, sortColumn, sortDesc]);

  useEffect(() => {
    getOrganizations();
  }, [getOrganizations, orgIndex]);
  return (
    <div className={styles['orgs-management-wrapper']}>
      <SEOHelmet title="Organizations Management" />
      <h1>Organizations Management </h1>

      {loading ? (
        <MsgParagraph>Loading...</MsgParagraph>
      ) : (
        <>
          <Modal
            title={`${openModal === 'new' ? 'Add' : 'Edit'} Organization`}
            isOpen={openModal !== null}
            onClose={() => {
              setOpenModal(null);
              setIsSSOEnabled(false);
              setIsGuideVisible(false);
              setSelectedOrg(undefined);
            }}
            size={openModal === 'new' && isGuideVisible ? 'wide' : 'medium'}
          >
            <OrganizationForm
              organization={selectedOrg}
              onSubmit={() => {
                setOpenModal(null);
                setIsSSOEnabled(false);
                setIsGuideVisible(false);
                setSelectedOrg(undefined);
                getOrganizations();
                refreshSession();
              }}
              onSSOToggle={setIsSSOEnabled}
              onGuideToggle={setIsGuideVisible}
            />
          </Modal>
          <div className={styles['settings']}>
            <form onSubmit={onSearch}>
              <input
                type="search"
                id="search"
                name="search"
                placeholder="Type to filter by name..."
                onChange={onSearch}
                required
              />
            </form>
            <Button
              variation="secondary"
              onClick={() => {
                setSelectedOrg(undefined);
                setOpenModal('new');
              }}
            >
              Add Organization
            </Button>
          </div>
          {query !== '' && status === 'success' && sortedOrgs.length === 0 ? (
            <MsgParagraph>
              Search for<strong>"{query}"</strong> returned no results.
            </MsgParagraph>
          ) : (
            <>
              <p className={styles['counter']}>
                {query
                  ? `Filter matches ${filteredOrgs.length} of ${orgs.length} Organizations`
                  : `${orgs.length} Total Organizations`}
              </p>
              <div className={styles['table-wrapper']}>
                <table>
                  <thead>
                    <tr>
                      {columns.map(({ label, value }) => (
                        <th key={value}>
                          <SortingButton
                            column={value}
                            label={label}
                            currentColumn={sortColumn}
                            desc={sortDesc}
                            currentDesc={sortDesc}
                            onClick={() => onSortingChange(value)}
                          />
                        </th>
                      ))}
                      <th>Seats</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrgs.map((org) => {
                      const {
                        created_at,
                        name,
                        id,
                        slug,
                        suggestions,
                        seat_limit,
                        seats_used,
                      } = org;
                      return (
                        <tr key={id} id={id}>
                          <td className={styles['cell-date']}>
                            {created_at?.split('T')[0]}
                          </td>
                          <td className={styles['cell-name']}>{name}</td>
                          <td className={styles['cell-slug']}>{slug}</td>
                          <td className={styles['cell-suggestions']}>
                            <ul>
                              {suggestions.map((suggestion, index) => (
                                <li key={index}>{suggestion}</li>
                              ))}
                            </ul>
                          </td>
                          <td className={styles['cell-seats']}>
                            <SeatInfo
                              seatsUsed={seats_used ?? 0}
                              seatLimit={seat_limit ?? 20}
                              compact
                            />
                          </td>
                          <td className={styles['cell-actions']}>
                            <Button
                              variation="secondary"
                              className={styles['edit-btn']}
                              onClick={() => {
                                // setSelectedUser(user);
                                setSelectedOrg(org);
                                setOpenModal('edit');
                              }}
                            >
                              <GearIcon />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

interface SortingButtonProps {
  column: keyof Organization;
  label: string;
  currentColumn: keyof Organization;
  desc: boolean;
  currentDesc: boolean;
  onClick: () => void;
}
const SortingButton: FC<SortingButtonProps> = ({
  column,
  label,
  currentColumn,
  currentDesc,
  onClick,
}) => {
  return (
    <button
      className={classnames(styles['sorting-button'], {
        [styles['active-col']]: currentColumn === column,
        [styles['is-asc']]: !currentDesc,
      })}
      onClick={onClick}
    >
      {label}

      <ChevronLeftIcon className={styles['sorting-order-icon']} />
    </button>
  );
};

export default OrgsManagement;
